import { useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import "./personal-board-style.css";
import UserService from "../../../service/user-service";
import AuthService from "../../../service/auth-service";
import GoogleLoginDialog from "../../dialog/google-login/GoogleLoginDialog";
import ConfirmDialog from "../../dialog/confirm/confirm-dialog";
import { compressImage } from "../../../utils/image-compress";
import SiteFooter from "../../custom/site-footer/site-footer";
import useIsMobile from "../../../hooks/useIsMobile";

const PersonalBoardComponent = ({
  setShowServiceMenu,
  setShowPersonalMenu,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const registerFrom = location.pathname;

  const isMobile = useIsMobile();
  const [userInfo, setUserInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  //—— Auth gating（與其他頁同 pattern）——
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);

  //實際撈個人資料（給 mount + login success 兩處共用）
  const fetchProfile = useCallback(async () => {
    try {
      const response = await UserService.getPersonalInfo();
      if (response.data.status) {
        setUserInfo(response.data.data);
      }
    } catch (e) {
      console.error("獲取資料發生錯誤：", e);
    }
  }, []);

  //—— mount 時的 auth 守門 ——
  //  未登入 → 跳 login dialog
  //  已登入但 validCode != 2 → 跳註冊提示
  //  validCode === "2" → 撈資料
  useEffect(() => {
    const isLogin = AuthService.getLoginStatus();
    const profile = AuthService.getUserProfile();
    if (!isLogin || !profile) {
      setShowLoginDialog(true);
      return;
    }
    if (profile.userValidCode !== "2") {
      setShowRegisterPrompt(true);
      return;
    }
    fetchProfile();
  }, [fetchProfile]);

  const handleLoginSuccess = async ({ idToken } = {}) => {
    if (!idToken) return;
    try {
      const res = await AuthService.firebaseLogin(idToken);
      if (res.data.status) {
        AuthService.login(res.data.data);
        setShowLoginDialog(false);
        if (res.data.data.userValidCode !== "2") {
          navigate("/register", { state: { from: registerFrom } });
        } else {
          //完整使用者 → 直接撈
          fetchProfile();
        }
      }
    } catch (e) {
      console.error("[PersonalBoard] firebase-login 失敗:", e);
    }
  };

  //換大頭貼：選檔 → 上傳 → 同步 userInfo state 與 AuthService（讓 nav 也跟著更新）
  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; //清掉 input，使用者選同一張可以再觸發
    if (!file) return;

    if (file.size > 3 * 1024 * 1024) {
      alert("檔案太大（上限 3MB）");
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      alert("僅支援 PNG / JPG / WebP");
      return;
    }

    try {
      setUploading(true);
      //上傳前壓到 ~256KB / 512px，省 bandwidth、storage 與後續顯示流量
      const compressed = await compressImage(file, "avatar");
      const res = await UserService.uploadUserPhoto(compressed);
      if (res.data.status) {
        const newPhoto = res.data.data.userPhoto;
        setUserInfo((prev) => ({ ...prev, userPhoto: newPhoto }));
        const profile = AuthService.getUserProfile() || {};
        AuthService.updateUserInfo({ ...profile, userPhoto: newPhoto });
      } else {
        alert(res.data.message || "更新失敗");
      }
    } catch (err) {
      console.error("上傳大頭貼失敗：", err);
      alert(err?.response?.data?.message || "上傳失敗，請稍後再試");
    } finally {
      setUploading(false);
    }
  };

  const infoCard = (
    <div className="personal-card">
      <div className="photo-editor">
        <img
          src={userInfo?.userPhoto || "/images/ic-default-userphoto.png"}
          alt="user photo"
          referrerPolicy="no-referrer"
          onError={(e) => {
            if (!e.target.src.endsWith("/images/ic-default-userphoto.png")) {
              e.target.src = "/images/ic-default-userphoto.png";
            }
          }}
        />
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handlePhotoChange}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? "上傳中…" : "更換大頭貼"}
        </button>
      </div>
      <div className="item-info">
        <span>帳號</span>
        <span className="content">{userInfo?.userEmail}</span>
      </div>
      <div className="item-info">
        <span>姓名</span>
        <span className="content">{userInfo?.userName}</span>
      </div>
      <div className="item-info">
        <span>性別</span>
        <span className="content">
          {userInfo?.userGender === "1"
            ? "男"
            : userInfo?.userGender === "0"
              ? "女"
              : ""}
        </span>
      </div>
      <div className="item-info">
        <span>出生日期</span>
        <span className="content">{userInfo?.userBirthDate}</span>
      </div>
      <div className="item-info">
        <span>出生時辰/真太陽時</span>
        <span className="content">{`${userInfo?.userBirthTime}時 ${userInfo?.userBirthClockTime}`}</span>
      </div>
      <div className="item-info">
        <span>出生地</span>
        <span className="content">{userInfo?.userBirthPlace}</span>
      </div>
      <div className="item-info">
        <span>出生地-經度</span>
        <span className="content">{userInfo?.userBirthLongitude}</span>
      </div>
      <div className="item-info">
        <span>出生地-緯度</span>
        <span className="content">{userInfo?.userBirthLatitude}</span>
      </div>
      <button className="edit">
        <NavLink
          className="nav-link"
          to="/personal-editor"
          state={{ ...userInfo }}
        >
          編輯個人檔案
        </NavLink>
      </button>
    </div>
  );

  return (
    <div
      className="personal-board-page"
      onMouseEnter={() => {
        setShowServiceMenu(false);
        setShowPersonalMenu(false);
      }}
    >
      {/* 電腦版佈局 */}
      {!isMobile && (
        <div className="web-main">
          <main>
            <div className="title">
              <img src="../images/ic-personal.png"></img>
              <span>個人檔案</span>
            </div>
            {infoCard}
          </main>

          <SiteFooter />
        </div>
      )}

      {/* 手機版佈局 */}
      {isMobile && (
        <div className="mobile-main">
          <main>
            <div className="title">
              <img src="../images/ic-personal.png"></img>
              <span>個人檔案</span>
            </div>
            {infoCard}
          </main>

          <SiteFooter />
        </div>
      )}

      {/* 未登入訪客點進來：跳登入；關掉 → 留在當前頁（本頁未登入無內容可顯示）*/}
      {showLoginDialog && (
        <GoogleLoginDialog
          open={showLoginDialog}
          onClose={() => {
            setShowLoginDialog(false);
          }}
          onSuccess={handleLoginSuccess}
        />
      )}

      {/* 已登入但未完成註冊：跳註冊提示；關掉 → 留在當前頁 */}
      <ConfirmDialog
        open={showRegisterPrompt}
        onClose={() => {
          setShowRegisterPrompt(false);
        }}
        onSuccess={() => {
          setShowRegisterPrompt(false);
          navigate("/register", { state: { from: registerFrom } });
        }}
        title="尚未完成註冊"
        message="個人檔案需要完成註冊才能查看，<br>是否前往註冊頁完成？"
        confirmTxt="前往註冊"
      />
    </div>
  );
};

export default PersonalBoardComponent;
